/**
 * excursionExport — the departure leg (05-VERTICAL-SLICE §VS-1).
 *
 * Sends a set of items (and the canonical edges among them) to a Miro board,
 * registers every created external object in entity_external_ids, snapshots
 * the departure baseline (built by the SAME board-read + snapshot path the
 * return uses, so the diff is self-consistent), and records the excursion
 * with its honest fidelity manifest.
 */

import { miroExcursionExport, miroExcursionReadBoard } from './miroExcursionApi';
import { registerExternalId } from '../connectors';
import {
  loadFieldMappingConfig,
  getColorRulesForEntity,
} from '../fieldMappingRegistry';
import type { Item } from '../../types/prioritization/lifecycle';
import type { ItemRelationshipEdge } from '../../types/prioritization/itemRelationships';
import {
  MIRO_CARD_DESCRIPTION_THRESHOLD,
  MIRO_CATEGORY_TO_COLOR,
  MIRO_DEFAULT_STICKY_COLOR,
  MIRO_EDGE_STROKE,
  MIRO_PROFILE,
} from './profiles/miro';
import { buildMiroSnapshot } from './miroSnapshot';
import {
  createExcursion,
  type ConnectorExcursion,
  type FidelityManifestEntry,
} from './excursionStore';

export interface DepartureInput {
  workspaceId: string;
  boardName: string;
  items: Item[];
  /** Confirmed/active canonical edges whose two endpoints are both in `items`. */
  edges: ItemRelationshipEdge[];
  collectionId?: string;
  createdBy?: string;
}

export interface DepartureResult {
  excursion: ConnectorExcursion;
  boardUrl: string;
  /** Engine-reported per-object failures (never silently dropped). */
  errors: string[];
}

/**
 * Declared loss, computed BEFORE export so the user sees it at departure
 * (02-DESIGN §2.4). Derived from the profile fidelity table + the actual
 * shape of the departing scope.
 */
export function computeFidelityManifest(
  items: Item[],
  edges: ItemRelationshipEdge[],
): FidelityManifestEntry[] {
  const manifest: FidelityManifestEntry[] = [];

  const unencodableEdges = edges.filter(e => !(e.type in MIRO_EDGE_STROKE));
  if (unencodableEdges.length > 0) {
    const types = [...new Set(unencodableEdges.map(e => e.type))].join(', ');
    manifest.push({
      plane: 'relationships',
      note: `${unencodableEdges.length} edge(s) of type ${types} have no Miro connector form and will not travel — they re-attach on return.`,
    });
  }

  const uncoloredCategories = [
    ...new Set(
      items
        .map(i => i.itemCategory)
        .filter((c): c is NonNullable<typeof c> => Boolean(c))
        .filter(c => !(c in MIRO_CATEGORY_TO_COLOR)),
    ),
  ];
  if (uncoloredCategories.length > 0) {
    manifest.push({
      plane: 'semantics',
      note: `Categories without a mapped sticky color (${uncoloredCategories.join(', ')}) export as ${MIRO_DEFAULT_STICKY_COLOR} — category will not survive visually for those items.`,
    });
  }

  const edgeNote = MIRO_PROFILE.fidelity.relationships.notes;
  if (edges.length > 0 && edgeNote) {
    manifest.push({ plane: 'relationships', note: edgeNote });
  }
  manifest.push({
    plane: 'semantics',
    note: 'Evidence bindings, decisions, and open questions do not travel; they re-attach on return (context inflation).',
  });

  return manifest;
}

/** Item → dialect object encoding (03-PROFILE §5). */
export function encodeItemsForMiro(items: Item[]): Array<{
  internalId: string;
  title: string;
  description?: string;
  fillColor?: string;
  kind: 'sticky' | 'card';
}> {
  return items.map(item => {
    const description = item.description?.trim() || undefined;
    const kind: 'sticky' | 'card' =
      description && description.length > MIRO_CARD_DESCRIPTION_THRESHOLD ? 'card' : 'sticky';
    const fillColor = item.itemCategory
      ? MIRO_CATEGORY_TO_COLOR[item.itemCategory] ?? MIRO_DEFAULT_STICKY_COLOR
      : MIRO_DEFAULT_STICKY_COLOR;
    return { internalId: item.id, title: item.title, description, fillColor, kind };
  });
}

/** Edge → captioned connector encoding. Caption = canonical name (round-trips explicitly). */
export function encodeEdgesForMiro(edges: ItemRelationshipEdge[]): Array<{
  sourceInternalId: string;
  targetInternalId: string;
  caption?: string;
  strokeStyle?: 'normal' | 'dashed' | 'dotted';
}> {
  return edges
    .filter(e => e.type in MIRO_EDGE_STROKE)
    .map(e => ({
      sourceInternalId: e.sourceItemId,
      targetInternalId: e.targetItemId,
      caption: e.type,
      strokeStyle: MIRO_EDGE_STROKE[e.type],
    }));
}

export async function departForMiro(input: DepartureInput): Promise<{ data: DepartureResult | null; error: string | null }> {
  const { workspaceId, boardName, items, edges, collectionId, createdBy } = input;
  if (items.length === 0) return { data: null, error: 'Nothing to send — the scope has no items.' };

  const manifest = computeFidelityManifest(items, edges);

  let exported: Awaited<ReturnType<typeof miroExcursionExport>>;
  try {
    exported = await miroExcursionExport({
      workspaceId,
      boardName,
      frameTitle: boardName,
      objects: encodeItemsForMiro(items),
      relations: encodeEdgesForMiro(edges),
    });
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Miro export failed' };
  }

  // Identity spine: every created external object → entity_external_ids
  // (the registration the legacy Miro import path never did).
  for (const object of exported.objects) {
    await registerExternalId({
      workspaceId,
      entityType: 'item',
      internalId: object.internalId,
      connectorType: 'miro',
      externalId: object.externalId,
      externalUrl: `${exported.boardUrl}?moveToWidget=${object.externalId}`,
    });
  }

  // Baseline = the board as the return leg will see it, with internal ids
  // stamped — same read + snapshot code path as the return diff.
  const internalByExternal = new Map(exported.objects.map(o => [o.externalId, o.internalId]));
  let baseline;
  try {
    const read = await miroExcursionReadBoard(workspaceId, exported.boardId);
    const config = await loadFieldMappingConfig(workspaceId, 'miro');
    baseline = buildMiroSnapshot(
      read,
      {
        relationshipMap: config.relationshipMap,
        typeMap: config.typeMap,
        colorRules: getColorRulesForEntity(config.miroColorMaps ?? [], 'items'),
      },
      { scopeFrameId: exported.frameId ?? undefined },
    );
    for (const object of baseline.objects) {
      const internalId = internalByExternal.get(object.externalId);
      if (internalId) object.internalId = internalId;
    }
  } catch (err) {
    return {
      data: null,
      error: `Board exported (${exported.boardUrl}) but baseline read failed — excursion not recorded: ${err instanceof Error ? err.message : 'unknown error'}`,
    };
  }

  const { data: excursion, error } = await createExcursion({
    workspaceId,
    connectorType: 'miro',
    externalContainerId: exported.boardId,
    externalContainerUrl: exported.boardUrl,
    scope: {
      ...(collectionId ? { collectionId } : {}),
      itemIds: items.map(i => i.id),
      ...(exported.frameId ? { scopeFrameId: exported.frameId } : {}),
    },
    baseline,
    fidelityManifest: manifest,
    createdBy,
  });
  if (error || !excursion) return { data: null, error: error ?? 'Failed to record excursion' };

  return { data: { excursion, boardUrl: exported.boardUrl, errors: exported.errors }, error: null };
}
